import { useTranslation } from 'react-i18next';

import { BaseIngredientTabProps } from 'components/desktop/BaseIngredient/BaseIngredientComponent';
import 'components/desktop/BaseIngredient/BaseIngredientComponent.scss';
import FoldableCard from 'components/desktop/_general/Card/FoldableCard/FoldableCard';
import CharacteristicRadioSelect from 'components/desktop/_general/CharacteristicSelect/CharacteristicRadioSelect';
import { showConfirmationDialog } from 'components/desktop/_general/ConfirmationDialog/ConfirmationDialog';
import { EAlertType } from 'enums/alertType';
import { Characteristic } from 'enums/characteristic';
import PERMISSIONS from 'enums/permissions';
import { fireAlert } from 'functions/fireAlert';
import { Optional } from 'functions/promiseExtensions';
import { arePermissionsInUserPermissions } from 'functions/tokenFunctions';
import * as Co2FootprintList from 'types/Co2Footprint/Co2FootprintList';
import * as NutrientValueList from 'types/NutrientValue/NutrientValueList';
import * as UnitWeightList from 'types/UnitWeight/UnitWeightList';

export const BaseIngredientCharacteristicsCard = ({
	state,
	handlers,
}: BaseIngredientTabProps): JSX.Element => {
	const { t } = useTranslation();

	const isBaseIngredientEditor: boolean = arePermissionsInUserPermissions([
		PERMISSIONS.WRITEBASEINGREDIENT,
	]);

	const handleRecalculateNutrients = (
		characteristic: Characteristic,
		nutrientValues: NutrientValueList.Type
	): Optional<{
		nutrientValue: NutrientValueList.Type;
		calories: number;
		unitWeights: UnitWeightList.Type;
		carbonDioxide: Co2FootprintList.Type;
	}> => {
		const result: NutrientValueList.Type = [];
		let calories = state.calories;
		let unitWeights = state.unitWeights;
		let carbonDioxide = state.co2Footprints;
		const factor = characteristic === Characteristic.Fluid ? state.density : 1 / state.density;
		for (const nutrientValue of nutrientValues) {
			result.push({
				...nutrientValue,
				value: nutrientValue.value * factor,
			});
		}
		calories = state.calories * factor;
		unitWeights = state.unitWeights.map((x) => ({
			...x,
			weight: x.weight * factor,
		}));
		carbonDioxide = state.co2Footprints.map((x) => ({
			...x,
			value: x.value * factor,
		}));
		fireAlert(
			EAlertType.success,
			t('alerts:ALERT_RECALCULATE_AUTOMATIC_TITLE'),
			t('alerts:ALERT_RECALCULATE_AUTOMATIC_SUCCESS')
		);

		return Optional.Just({
			nutrientValue: result,
			calories,
			unitWeights,
			carbonDioxide: carbonDioxide,
		});
	};

	const setToSolidNonQuantifiable = (recalculate: boolean): void => {
		handlers.setCharacteristic(Characteristic.SolidNonQuantifiable);
		if (state.characteristic !== Characteristic.Fluid) return;
		if (!recalculate) return;
		handleRecalculateNutrients(Characteristic.SolidNonQuantifiable, state.nutrientValues)
			.do((x) => {
				handlers.setNutrientValues(x.nutrientValue);
				handlers.setCalories(x.calories);
				handlers.setUnitWeights(x.unitWeights);
				handlers.setCo2Footprints(x.carbonDioxide);
			})
			.orElseDo(() => {
				if (state.characteristic) handlers.setCharacteristic(state.characteristic);
			});
	};

	const shouldRecalculate = async () => {
		const answer = await showConfirmationDialog(
			t('alerts:ALERT_RECALCULATE'),
			t('alerts:ALERT_RECALCULATE_OK'),
			t('alerts:ALERT_RECALCULATE_NO')
		);

		return answer.isConfirmed;
	};

	const handleCharacteristicChange = async (checked: boolean, elementId: string) => {
		let recalculate: boolean = false;

		switch (elementId) {
			case Characteristic.Fluid:
				if (state.density === 0) {
					fireAlert(
						EAlertType.danger,
						t('alerts:ALERT_INVALID_INPUT_DATA'),
						t('alerts:ALERT_DENSIITY_NOT_VALID')
					);
					break;
				}
				recalculate = await shouldRecalculate();

				if (checked) {
					handlers.setCharacteristic(Characteristic.Fluid);
					if (recalculate) {
						handleRecalculateNutrients(Characteristic.Fluid, state.nutrientValues)
							.do((x) => {
								handlers.setNutrientValues(x.nutrientValue);
								handlers.setCalories(x.calories);
								handlers.setUnitWeights(x.unitWeights);
								handlers.setCo2Footprints(x.carbonDioxide);
							})
							.orElseDo(() => {
								if (state.characteristic)
									handlers.setCharacteristic(state.characteristic);
							});
					}
				}
				break;
			case Characteristic.SolidNonQuantifiable:
				if (checked) {
					if (state.characteristic == Characteristic.Fluid) {
						recalculate = await shouldRecalculate();
						setToSolidNonQuantifiable(recalculate);
					} else {
						setToSolidNonQuantifiable(false);
					}
				}
				break;
			case Characteristic.SolidQuantifiable:
				if (checked) {
					handlers.setCharacteristic(Characteristic.SolidQuantifiable);
					if (state.characteristic == Characteristic.Fluid) {
						recalculate = await shouldRecalculate();
						if (recalculate) {
							handleRecalculateNutrients(
								Characteristic.SolidQuantifiable,
								state.nutrientValues
							)
								.do((x) => {
									handlers.setNutrientValues(x.nutrientValue);
									handlers.setCalories(x.calories);
									handlers.setUnitWeights(x.unitWeights);
									handlers.setCo2Footprints(x.carbonDioxide);
								})
								.orElseDo(() => {
									if (state.characteristic)
										handlers.setCharacteristic(state.characteristic);
								});
						}
					}
				}
				break;
		}
	};

	return (
		<FoldableCard
			defaultOpen={true}
			dataComponentType="baseingredient-characteristics-card"
			title={t('_general:CHARACTERISTICS')}
			content={
				<>
					<div className="card-body">
						<CharacteristicRadioSelect
							checked={state.characteristic}
							disabled={!isBaseIngredientEditor}
							handleCharacteristicChange={handleCharacteristicChange}
						/>
					</div>
				</>
			}
		/>
	);
};
